import { useMutation } from '@apollo/client';
import { getTimeOfDay } from '@focuslee/shared';
import { translateTemplateText, useLiquidGlassPress } from '@focuslee/shared-mini-app';
import { Box } from '@mui/material';
import { hapticFeedback } from '@tma.js/sdk';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { addQuestCompletion } from '../../client/apollo';
import { CompleteQuestMutation, CompleteQuestMutationVariables } from '../../client/generated/graphql';
import { completeQuestMutation } from '../../client/mutations/completeQuest';
import { ListQuest, ListQuestCompletion } from '../../client/types';
import { getNextDueDate, mapTimeOfDayToGraphQLTimeOfDay } from '../../helpers/quest';
import { CommonIcon } from '../common/CommonIcon';

export type QuestCompleteButtonProps = {
    quest: ListQuest;
    onComplete?: (completion: ListQuestCompletion | null | undefined) => void;
    onPressedChange?: (pressed: boolean) => void;
};

export const QuestCompleteButton = ({ quest, onComplete, onPressedChange }: QuestCompleteButtonProps) => {
    const { t } = useTranslation();
    const [completed, setCompleted] = useState(false);
    const [completeQuest, { loading }] = useMutation<CompleteQuestMutation, CompleteQuestMutationVariables>(
        completeQuestMutation,
        { fetchPolicy: 'no-cache' }
    );

    const disabled = loading || completed;

    const handleComplete = async () => {
        if (disabled) return;
        hapticFeedback.impactOccurred.ifAvailable('light');
        setCompleted(true);
        try {
            const newDueDate = getNextDueDate(quest);
            const timeOfDay = getTimeOfDay(quest.dueDate);
            const completionResult = await completeQuest({
                variables: {
                    questId: quest.id,
                    dueDate: newDueDate,
                    timerValueInMs: quest.timerValueInMs || undefined,
                    timeOfDay: mapTimeOfDayToGraphQLTimeOfDay(timeOfDay)
                }
            });

            const completionData = completionResult.data?.completeQuest;
            if (completionData) {
                addQuestCompletion(completionData);
                onComplete?.(completionData);
            }
        } catch (error) {
            console.error('Error completing quest:', error);
        } finally {
            setCompleted(false);
        }
    };

    const { pressed, onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onClick } = useLiquidGlassPress({
        disabled,
        preventDefault: true,
        onPressedChange,
        onActivate: handleComplete,
    });

    const icon = completed ? 'check_circle' : 'circle';
    const type = completed ? 'filled' : 'outlined';

    return (
        <Box
            component="button"
            type="button"
            aria-label={t('quest.completeNamed', { name: translateTemplateText(t, quest.templateId, 'name', quest.name) })}
            disabled={disabled}
            onClick={onClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerCancel}
            sx={{
                width: '100%',
                height: '100%',
                border: 'none',
                background: 'none',
                cursor: disabled ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                WebkitTapHighlightColor: 'transparent',
                outline: 'none',
                bgcolor: pressed ? 'color-mix(in srgb, var(--tg-theme-text-color) 8%, transparent)' : 'transparent',
                transition: 'background-color 0.12s ease',
            }}
        >
            <CommonIcon icon={icon} type={type} size={26} color="var(--tgui--button_color)" />
        </Box>
    );
};
